// src/pages/products/ProductDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Package,
  Tag,
  DollarSign,
  Warehouse,
  Building2,
  Box,
  Barcode,
  Hash,
  Weight,
  Ruler,
  Eye,
  EyeOff,
  Calendar,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  PackageX
} from 'lucide-react';
import { getProduct } from '../../services/api/products';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';
import { formatDate, formatTime } from '../../utils/dateUtils';
import BundleBadge from '../../components/ui/BundleBadge/BundleBadge';
import { round2 } from '../../components/product/BundleEditor/bundleUtils';
import styles from './ProductDetailsPage.module.css';

const getBundleWeight = (bundleItems = []) => round2(bundleItems.reduce((sum, item) => {
  const weight = parseFloat(item.componentVariation?.weight ?? item.componentProduct?.weight) || 0;
  return sum + weight * (parseFloat(item.quantity) || 0);
}, 0));

const ProductDetailsPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { t } = useLocalization();
  const { error } = useAlert();

  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [productNotFound, setProductNotFound] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [expandedShops, setExpandedShops] = useState({});
  const [expandedVariations, setExpandedVariations] = useState({});

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setIsLoading(true);
      const response = await getProduct(productId);
      setProduct(response.product);
    } catch (err) {
      console.error('Failed to fetch product:', err);
      if (err.response?.status === 404) {
        setProductNotFound(true);
      } else {
        error(t('products.failedToLoadProduct'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleShopExpansion = (shopId) => {
    setExpandedShops(prev => ({
      ...prev,
      [shopId]: !prev[shopId]
    }));
  };

  const toggleVariationExpansion = (variationId) => {
    setExpandedVariations(prev => ({
      ...prev,
      [variationId]: !prev[variationId]
    }));
  };

  const getVisibilityInfo = (visibility) => {
    switch (visibility) {
      case 'public':
        return { icon: Eye, label: t('products.public'), className: styles.public };
      case 'draft':
        return { icon: EyeOff, label: t('products.draft'), className: styles.draft };
      case 'trash':
        return { icon: PackageX, label: t('products.trash'), className: styles.trash };
      default:
        return { icon: EyeOff, label: visibility, className: styles.draft };
    }
  };

  const getAllImages = () => {
    if (!product?.images) return [];
    return product.images.sort((a, b) => {
      if (a.isMain) return -1;
      if (b.isMain) return 1;
      return 0;
    });
  };

  const getPriceRange = (shops) => {
    if (!shops || shops.length === 0) return null;
    const prices = shops.map(s => s.price).filter(p => p != null);
    if (prices.length === 0) return null;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return { min, max, isSingle: min === max };
  };

  const getTotalStock = (stocks) => {
    if (!stocks || stocks.length === 0) return 0;
    return stocks.reduce((sum, stock) => sum + (stock.quantity || 0), 0);
  };

  const getStockByWarehouse = (stocks) => {
    if (!stocks || stocks.length === 0) return [];
    return stocks.map(stock => ({
      warehouseId: stock.warehouseId,
      quantity: stock.quantity || 0
    }));
  };

  if (productNotFound) {
    return (
      <div className={styles.productDetailsPage}>
        <div className={styles.pageHeader}>
          <button onClick={() => navigate('/products')} className={styles.backButton}>
            <ArrowLeft size={20} />
            {t('products.backToProducts')}
          </button>
        </div>
        <div className={styles.errorState}>
          <AlertCircle size={48} className={styles.errorIcon} />
          <h3>Product Not Found</h3>
          <p>The product you're looking for doesn't exist or has been removed.</p>
          <button onClick={() => navigate('/products')} className={styles.primaryButton}>
            {t('products.backToProducts')}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !product) {
    return (
      <div className={styles.productDetailsPage}>
        <div className={styles.pageHeader}>
          <button onClick={() => navigate('/products')} className={styles.backButton}>
            <ArrowLeft size={20} />
            {t('products.backToProducts')}
          </button>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <h3>{t('products.loadingData')}</h3>
          <p>{t('products.loadingDataDesc')}</p>
        </div>
      </div>
    );
  }

  const images = getAllImages();
  const visibilityInfo = getVisibilityInfo(product.visibility);
  const priceRange = product.type === 'variable' 
    ? getPriceRange(product.productShops) 
    : null;

  return (
    <div className={styles.productDetailsPage}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <button onClick={() => navigate('/products')} className={styles.backButton}>
          <ArrowLeft size={20} />
          {t('products.backToProducts')}
        </button>
        <Link to={`/products/${productId}/edit`} className={styles.editButton}>
          <Edit size={18} />
          {t('products.editProduct')}
        </Link>
      </div>

      <div className={styles.contentWrapper}>
        {/* Left Column - Image Gallery */}
        <div className={styles.leftColumn}>
          <div className={styles.imageSection}>
            <div className={styles.mainImage}>
              {images.length > 0 ? (
                <img
                  src={getImageUrl(images[selectedImage].path)}
                  alt={product.name}
                  onError={handleImageError}
                />
              ) : (
                <div className={styles.noImage}>
                  <Package size={64} />
                  <span>No Image</span>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className={styles.thumbnails}>
                {images.map((img, index) => (
                  <div
                    key={img.id}
                    className={`${styles.thumbnail} ${selectedImage === index ? styles.active : ''}`}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img
                      src={getImageUrl(img.path)}
                      alt={`${product.name} ${index + 1}`}
                      onError={handleImageError}
                    />
                    {img.isMain && <span className={styles.mainBadge}>Main</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Product Details */}
        <div className={styles.rightColumn}>
          {/* Basic Info Section */}
          <div className={styles.infoSection}>
            <div className={styles.titleRow}>
              <h1>{product.name}</h1>
              <div className={`${styles.visibilityBadge} ${visibilityInfo.className}`}>
                <visibilityInfo.icon size={14} />
                <span>{visibilityInfo.label}</span>
              </div>
            </div>

            {product.description && (
              <div 
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            )}

            {/* Quick Info Grid */}
            <div className={styles.quickInfoGrid}>
              <div className={styles.infoCard}>
                <div className={styles.infoLabel}>
                  <Box size={16} />
                  {t('products.productTypeShort')}
                </div>
                <div className={styles.infoValue}>
                  {product.type === 'bundle'
                    ? <BundleBadge variant="list" />
                    : product.type === 'variable'
                      ? t('products.variableProductShort')
                      : t('products.simpleProductShort')}
                </div>
              </div>

              {product.sku && (
                <div className={styles.infoCard}>
                  <div className={styles.infoLabel}>
                    <Hash size={16} />
                    {t('products.sku')}
                  </div>
                  <div className={styles.infoValue}>{product.sku}</div>
                </div>
              )}

              {product.barcode && (
                <div className={styles.infoCard}>
                  <div className={styles.infoLabel}>
                    <Barcode size={16} />
                    {t('products.barcode')}
                  </div>
                  <div className={styles.infoValue}>{product.barcode}</div>
                </div>
              )}

              {product.costPrice && (
                <div className={styles.infoCard}>
                  <div className={styles.infoLabel}>
                    <DollarSign size={16} />
                    {t('products.costPrice')}
                  </div>
                  <div className={styles.infoValue}>{product.costPrice.toFixed(2)}</div>
                </div>
              )}

              {product.type === 'bundle' && (
                <div className={styles.infoCard}>
                  <div className={styles.infoLabel}>
                    <Weight size={16} />
                    {t('products.totalWeight')}
                  </div>
                  <div className={styles.infoValue}>{getBundleWeight(product.bundleItems)} kg</div>
                </div>
              )}

              {product.weight && product.type !== 'bundle' && (
                <div className={styles.infoCard}>
                  <div className={styles.infoLabel}>
                    <Weight size={16} />
                    {t('products.weight')}
                  </div>
                  <div className={styles.infoValue}>{product.weight} kg</div>
                </div>
              )}

              {product.dimensions && (
                <div className={styles.infoCard}>
                  <div className={styles.infoLabel}>
                    <Ruler size={16} />
                    Dimensions
                  </div>
                  <div className={styles.infoValue}>
                    {product.dimensions.length} × {product.dimensions.width} × {product.dimensions.height} cm
                  </div>
                </div>
              )}
            </div>

            {/* Categories, Tags, Brand */}
            <div className={styles.taxonomySection}>
              {product.productCategories && product.productCategories.length > 0 && (
                <div className={styles.taxonomyGroup}>
                  <span className={styles.taxonomyLabel}>Categories:</span>
                  <div className={styles.tagsList}>
                    {product.productCategories.map(cat => (
                      <span key={cat.id} className={styles.categoryTag}>
                        {cat.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {product.productTags && product.productTags.length > 0 && (
                <div className={styles.taxonomyGroup}>
                  <span className={styles.taxonomyLabel}>Tags:</span>
                  <div className={styles.tagsList}>
                    {product.productTags.map(tag => (
                      <span key={tag.id} className={styles.productTag}>
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {product.ProductBrand && (
                <div className={styles.taxonomyGroup}>
                  <span className={styles.taxonomyLabel}>Brand:</span>
                  <span className={styles.brandName}>{product.ProductBrand.name}</span>
                </div>
              )}
            </div>

            {/* Attributes (non-variation) */}
            {product.attributeSelections && product.attributeSelections.length > 0 && (
              <div className={styles.attributesSection}>
                <h3>Attributes</h3>
                <div className={styles.attributesList}>
                  {product.attributeSelections.map(attr => (
                    <div key={attr.id} className={styles.attributeItem}>
                      <span className={styles.attributeName}>
                        {attr.ProductAttribute.name}
                        {attr.usedForVariations && (
                          <span className={styles.variationBadge}>Used for variations</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className={styles.metadataSection}>
              <div className={styles.metadataItem}>
                <Calendar size={14} />
                <span>Created: {formatDate(product.createdAt)} at {formatTime(product.createdAt)}</span>
              </div>
              <div className={styles.metadataItem}>
                <Calendar size={14} />
                <span>Updated: {formatDate(product.updatedAt)} at {formatTime(product.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Pricing & Stock Section */}
          {product.type === 'bundle' ? (
            <div className={styles.pricingSection}>
              <h2>
                <DollarSign size={20} />
                {t('products.bundleShopPricing')}
              </h2>

              {product.productShops && product.productShops.length > 0 ? (
                <div className={styles.shopsList}>
                  {product.productShops.map(shop => (
                    <div key={shop.id} className={`${styles.shopCard} ${styles.shopCardWide}`}>
                      <div className={styles.shopHeader}>
                        <Building2 size={16} />
                        <span>Shop #{shop.shopId}</span>
                      </div>
                      <div className={styles.shopDetails}>
                        <div className={`${styles.bundleLine} ${styles.bundleLineHeader}`}>
                          <span>{t('ecomOrders.item')}</span>
                          <span>{t('products.componentQty')}</span>
                          <span>{t('ecomOrders.unitPrice')}</span>
                          <span>{t('ecomOrders.lineDiscount')}</span>
                          <span className={styles.bundleLineTotal}>{t('ecomOrders.lineTotal')}</span>
                        </div>
                        {(product.bundleItems || []).map(item => {
                          const sp = (item.shopPrices || []).find(p => String(p.shopId) === String(shop.shopId)) || {};
                          const qty = parseFloat(item.quantity) || 0;
                          const unit = parseFloat(sp.unitValue) || 0;
                          const discount = parseFloat(sp.lineDiscount) || 0;
                          const total = round2(qty * unit - discount);
                          return (
                            <div key={item.id} className={styles.bundleLine}>
                              <span className={styles.bundleLineName}>{item.componentProduct?.name}</span>
                              <span>× {qty}</span>
                              <span>{unit.toFixed(2)}</span>
                              <span className={styles.bundleLineDiscount}>{discount > 0 ? `− ${discount.toFixed(2)}` : ''}</span>
                              <span className={styles.bundleLineTotal}>
                                {total <= 0 && <span className={styles.freeTag}>{t('ecomOrders.free')}</span>}
                                {total.toFixed(2)}
                              </span>
                            </div>
                          );
                        })}
                        <div className={styles.priceRow}>
                          <span className={styles.label}>{t('products.bundlePrice')}:</span>
                          <span className={styles.price}>{(parseFloat(shop.price) || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.noData}>No shop pricing configured</p>
              )}

              <div className={styles.stockSection}>
                <h3>
                  <Warehouse size={18} />
                  Stock Information
                </h3>
                <div className={styles.stockSummary}>
                  <div className={styles.totalStock}>
                    <span className={styles.label}>{t('products.availableStock')}:</span>
                    <span className={styles.stockValue}>{product.stockQuantity ?? 0}</span>
                  </div>
                </div>
              </div>

              <div className={styles.stockSection}>
                <h3>
                  <Package size={18} />
                  {t('products.bundleComponents')}
                </h3>
                <div className={styles.componentStockList}>
                  {(product.bundleItems || []).map(item => {
                    const stocks = item.componentStocks || [];
                    const total = stocks.reduce((s, r) => s + r.quantity, 0);
                    return (
                      <div key={item.id} className={styles.componentStockCard}>
                        <div className={styles.componentStockHeader}>
                          <Package size={14} />
                          <span className={styles.warehouseLabel}>
                            {item.componentProduct?.name}
                            {item.componentVariation?.sku ? ` (${item.componentVariation.sku})` : ''}
                            {!item.componentVariation && item.componentProduct?.sku ? ` · ${item.componentProduct.sku}` : ''}
                          </span>
                          <span className={styles.stockQuantity}>× {parseFloat(item.quantity)}</span>
                        </div>
                        <div className={styles.totalStock}>
                          <span className={styles.label}>{t('products.componentStock')}:</span>
                          <span className={`${styles.stockValue} ${total <= 0 ? styles.stockOut : ''}`}>{total}</span>
                        </div>
                        <div className={styles.warehouseStocks}>
                          {stocks.map(s => (
                            <div key={s.warehouseId} className={styles.warehouseStock}>
                              <Warehouse size={14} />
                              <span className={styles.warehouseLabel}>{s.warehouseName || `Warehouse #${s.warehouseId}`}:</span>
                              <span className={`${styles.stockQuantity} ${s.quantity <= 0 ? styles.stockOut : ''}`}>{s.quantity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : product.type === 'simple' ? (
            // Simple Product
            <div className={styles.pricingSection}>
              <h2>
                <DollarSign size={20} />
                Pricing & Stock
              </h2>

              {/* Shop Pricing */}
              {product.productShops && product.productShops.length > 0 ? (
                <div className={styles.shopsList}>
                  {product.productShops.map(shop => {
                    const stockInfo = product.stocks?.find(s => s.warehouseId);
                    return (
                      <div key={shop.id} className={styles.shopCard}>
                        <div className={styles.shopHeader}>
                          <Building2 size={16} />
                          <span>Shop #{shop.shopId}</span>
                        </div>
                        <div className={styles.shopDetails}>
                          <div className={styles.priceRow}>
                            <span className={styles.label}>Sales Price:</span>
                            <span className={styles.price}>{shop.price.toFixed(2)}</span>
                          </div>
                          {shop.dropshippingPrice && (
                            <div className={styles.priceRow}>
                              <span className={styles.label}>Dropshipping:</span>
                              <span className={styles.price}>{shop.dropshippingPrice.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className={styles.noData}>No shop pricing configured</p>
              )}

              {/* Stock Information */}
              {product.stocks && product.stocks.length > 0 && (
                <div className={styles.stockSection}>
                  <h3>
                    <Warehouse size={18} />
                    Stock Information
                  </h3>
                  <div className={styles.stockSummary}>
                    <div className={styles.totalStock}>
                      <span className={styles.label}>Total Stock:</span>
                      <span className={styles.stockValue}>{getTotalStock(product.stocks)}</span>
                    </div>
                  </div>
                  <div className={styles.warehouseStocks}>
                    {product.stocks.map(stock => (
                      <div key={stock.id} className={styles.warehouseStock}>
                        <Warehouse size={14} />
                        <span className={styles.warehouseLabel}>Warehouse #{stock.warehouseId}:</span>
                        <span className={styles.stockQuantity}>{stock.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Variable Product
            <div className={styles.pricingSection}>
              <h2>
                <Package size={20} />
                Variations
              </h2>

              {/* Price Range Summary */}
              {priceRange && (
                <div className={styles.priceRangeSummary}>
                  <div className={styles.priceRangeCard}>
                    <span className={styles.label}>Price Range:</span>
                    <span className={styles.priceRange}>
                      {priceRange.isSingle 
                        ? priceRange.min.toFixed(2)
                        : `${priceRange.min.toFixed(2)} - ${priceRange.max.toFixed(2)}`}
                    </span>
                  </div>
                  <div className={styles.priceRangeCard}>
                    <span className={styles.label}>Total Stock:</span>
                    <span className={styles.stockValue}>
                      {product.variations?.reduce((sum, v) => 
                        sum + getTotalStock(v.stocks), 0) || 0}
                    </span>
                  </div>
                </div>
              )}

              {/* Variations List */}
              {product.variations && product.variations.length > 0 ? (
                <div className={styles.variationsList}>
                  {product.variations.map(variation => {
                    const isExpanded = expandedVariations[variation.id];
                    const varPriceRange = getPriceRange(variation.variationShops);
                    const varTotalStock = getTotalStock(variation.stocks);

                    return (
                      <div key={variation.id} className={styles.variationCard}>
                        <div 
                          className={styles.variationHeader}
                          onClick={() => toggleVariationExpansion(variation.id)}
                        >
                          <div className={styles.variationInfo}>
                            {variation.images && variation.images.length > 0 && (
                              <div className={styles.variationImage}>
                                <img
                                  src={getImageUrl(variation.images[0].path)}
                                  alt={variation.sku}
                                  onError={handleImageError}
                                />
                              </div>
                            )}
                            <div className={styles.variationMeta}>
                              <div className={styles.variationAttributes}>
                                {variation.attributeValues?.map((attr, idx) => (
                                  <span key={idx} className={styles.attributeValue}>
                                    {attr.value}
                                  </span>
                                ))}
                              </div>
                              {variation.sku && (
                                <span className={styles.variationSku}>SKU: {variation.sku}</span>
                              )}
                            </div>
                          </div>
                          <div className={styles.variationSummary}>
                            <div className={styles.summaryItem}>
                              <span className={styles.label}>Price:</span>
                              <span className={styles.value}>
                                {varPriceRange 
                                  ? (varPriceRange.isSingle 
                                    ? varPriceRange.min.toFixed(2)
                                    : `${varPriceRange.min.toFixed(2)}-${varPriceRange.max.toFixed(2)}`)
                                  : 'N/A'}
                              </span>
                            </div>
                            <div className={styles.summaryItem}>
                              <span className={styles.label}>Stock:</span>
                              <span className={styles.value}>{varTotalStock}</span>
                            </div>
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className={styles.variationDetails}>
                            {/* Basic Info */}
                            <div className={styles.variationBasicInfo}>
                              {variation.costPrice && (
                                <div className={styles.detailItem}>
                                  <span className={styles.label}>Cost Price:</span>
                                  <span className={styles.value}>{variation.costPrice.toFixed(2)}</span>
                                </div>
                              )}
                              {variation.weight && (
                                <div className={styles.detailItem}>
                                  <span className={styles.label}>Weight:</span>
                                  <span className={styles.value}>{variation.weight} kg</span>
                                </div>
                              )}
                              {variation.barcode && (
                                <div className={styles.detailItem}>
                                  <span className={styles.label}>Barcode:</span>
                                  <span className={styles.value}>{variation.barcode}</span>
                                </div>
                              )}
                            </div>

                            {/* Shop Pricing */}
                            {variation.variationShops && variation.variationShops.length > 0 && (
                              <div className={styles.variationShops}>
                                <h4>Shop Pricing</h4>
                                <div className={styles.shopPricingGrid}>
                                  {variation.variationShops.map(shop => (
                                    <div key={shop.id} className={styles.shopPricing}>
                                      <Building2 size={14} />
                                      <span className={styles.shopId}>Shop #{shop.shopId}</span>
                                      <span className={styles.shopPrice}>{shop.price.toFixed(2)}</span>
                                      {shop.dropshippingPrice && (
                                        <span className={styles.dropshipPrice}>
                                          DS: {shop.dropshippingPrice.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Stock by Warehouse */}
                            {variation.stocks && variation.stocks.length > 0 && (
                              <div className={styles.variationStock}>
                                <h4>Stock by Warehouse</h4>
                                <div className={styles.warehouseStocksGrid}>
                                  {variation.stocks.map(stock => (
                                    <div key={stock.id} className={styles.warehouseStockItem}>
                                      <Warehouse size={14} />
                                      <span className={styles.warehouseId}>WH #{stock.warehouseId}</span>
                                      <span className={styles.quantity}>{stock.quantity}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className={styles.noData}>No variations configured</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsPage;